import { useState, useEffect } from 'react';
import { TcpMinimumField } from '../components/crew/TcpMinimumField';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { PlaneTakeoff, Plus, Search, Gauge, Fuel, Users, Calendar, Wrench, AlertTriangle, Settings2, Trash2, RefreshCw, Clock, BarChart3, Shield, History, LayoutGrid, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuditHistoryModal } from '../components/AuditHistoryModal';
import { AircraftCostPreview } from '../components/AircraftCostPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bloque B0: capacidad estándar por tipo ICAO (se precarga al registrar; editable por matrícula)
const STANDARD_CAPACITY = {
    SF34: 34, 'SAAB 340': 34, AT72: 72, AT76: 72, AT75: 70, AT45: 48, AT43: 48, C208: 12, DH8D: 78, DH8C: 50, DH8B: 37,
    E145: 50, E170: 76, E175: 88, E190: 100, E195: 120, CRJ2: 50, CRJ7: 70, CRJ9: 90, A319: 144, A320: 180, A321: 220, B737: 149, B738: 189,
};
const standardCapacity = (type) => {
    const t = (type || '').toUpperCase().trim();
    return STANDARD_CAPACITY[t] || (t.includes('SAAB 340') || t.includes('SAAB340') ? 34 : 0);
};
export default function FleetPage() {
    const { token, hasRole } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [aircraft, setAircraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingTcpMin, setPendingTcpMin] = useState('');
    const [newAircraft, setNewAircraft] = useState({
        registration: '',
        aircraft_type: '',
        model: '',
        manufacturer: '',
        serial_number: '',
        year_manufactured: '',
        max_passengers: 0,
        max_fuel_kg: 0,
        fuel_unit: 'kg',
        mtow_kg: 0,
        // Auditoria 7 - Item 6/21: baseline (historial previo a AeroOps)
        baseline_flight_hours: 0,
        baseline_cycles: 0,
        baseline_block_hours: 0,
        mlw_kg: 0,
        mzfw_kg: 0,
        bow_kg: 0,
        base_airport: '',
        status: 'operational',
        is_used_aircraft: false,
        total_flight_hours: 0,
        total_block_hours: 0,
        total_cycles: 0,
        last_weight_date: '',
        check_history: {
            A: { last_date: '', last_hours: '', last_cycles: '' },
            B: { last_date: '', last_hours: '', last_cycles: '' },
            C: { last_date: '', last_hours: '', last_cycles: '' },
            D: { last_date: '', last_hours: '', last_cycles: '' },
        }
    });
    const [statusDialog, setStatusDialog] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [statusReason, setStatusReason] = useState('');
    // Editar aeronave completo (Audit 3 follow-up): dialogo con todos los campos
    const [editDialog, setEditDialog] = useState(null); // aeronave abierta
    const [editForm, setEditForm] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);
    const [auditAircraft, setAuditAircraft] = useState(null);
    const [seatTemplate, setSeatTemplate] = useState('');
    // Bug fix: configurar mapa de asientos desde Flota (mensaje "Configure en Flota" ahora tiene UI)
    const [seatConfigDialog, setSeatConfigDialog] = useState(null); // aeronave abierta
    const [seatTemplatesData, setSeatTemplatesData] = useState({});
    const [seatConfig, setSeatConfig] = useState({
        columns_left: 'A,C',
        columns_right: 'D,F',
        total_rows: 18,
        exit_rows: '',
        blocked_seats: '',
        template_key: '',
    });
    const [savingSeatConfig, setSavingSeatConfig] = useState(false);
    const [reportDialog, setReportDialog] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [healthData, setHealthData] = useState({});

    useEffect(() => {
        fetchAircraft();
        fetchHealth();
        fetchSeatTemplates();
    }, []);

    // Bug fix: cargar plantillas de asientos disponibles
    const fetchSeatTemplates = async () => {
        try {
            const res = await axios.get(`${API}/seats/templates`, { headers });
            setSeatTemplatesData(res.data || {});
        } catch { /* silent */ }
    };

    // Bug fix: abrir dialogo de configuracion de mapa de asientos
    const openSeatConfigDialog = async (ac) => {
        setSeatConfigDialog(ac);
        // Intentar cargar config actual
        try {
            const res = await axios.get(`${API}/aircraft/${ac.id}/seat-config`, { headers });
            const cfg = res.data?.seat_config;
            if (cfg && (cfg.columns_left || cfg.columns_right)) {
                setSeatConfig({
                    columns_left: (cfg.columns_left || []).join(','),
                    columns_right: (cfg.columns_right || []).join(','),
                    total_rows: cfg.total_rows || 18,
                    exit_rows: (cfg.exit_rows || []).join(','),
                    blocked_seats: (cfg.blocked_seats || []).join(','),
                    template_key: '',
                });
                return;
            }
        } catch { /* sin config previa */ }
        // Defaults sensatos
        setSeatConfig({ columns_left: 'A,C', columns_right: 'D,F', total_rows: 18, exit_rows: '', blocked_seats: '', template_key: '' });
    };

    // Bug fix: aplicar template al form
    const applySeatTemplate = (key) => {
        const tpl = seatTemplatesData[key];
        if (!tpl) return;
        setSeatConfig({
            columns_left: (tpl.columns_left || []).join(','),
            columns_right: (tpl.columns_right || []).join(','),
            total_rows: tpl.total_rows || 18,
            exit_rows: (tpl.exit_rows || []).join(','),
            blocked_seats: (tpl.blocked_seats || []).join(','),
            template_key: key,
        });
    };

    // Bug fix: guardar mapa de asientos
    const saveSeatConfig = async () => {
        if (!seatConfigDialog) return;
        const parseList = (str) => (str || '').split(',').map(s => s.trim()).filter(Boolean);
        const parseRows = (str) => parseList(str).map(n => parseInt(n, 10)).filter(n => !isNaN(n));
        const payload = {
            columns_left: parseList(seatConfig.columns_left).map(s => s.toUpperCase()),
            columns_right: parseList(seatConfig.columns_right).map(s => s.toUpperCase()),
            total_rows: parseInt(seatConfig.total_rows, 10) || 0,
            exit_rows: parseRows(seatConfig.exit_rows),
            blocked_seats: parseList(seatConfig.blocked_seats).map(s => s.toUpperCase()),
            sections: [],
        };
        if (!payload.columns_left.length && !payload.columns_right.length) {
            toast.error('Debe indicar al menos una columna (izquierda o derecha)');
            return;
        }
        if (!payload.total_rows || payload.total_rows < 1) {
            toast.error('Total de filas debe ser mayor a 0');
            return;
        }
        setSavingSeatConfig(true);
        try {
            await axios.put(`${API}/aircraft/${seatConfigDialog.id}/seat-config`, payload, { headers });
            toast.success('Mapa de asientos configurado');
            setSeatConfigDialog(null);
            fetchAircraft();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar configuración');
        } finally {
            setSavingSeatConfig(false);
        }
    };

    // Editar aeronave: abrir dialogo con datos actuales
    const openEditDialog = (ac) => {
        setEditDialog(ac);
        setEditForm({
            registration: ac.registration || '',
            aircraft_type: ac.aircraft_type || '',
            model: ac.model || '',
            manufacturer: ac.manufacturer || '',
            serial_number: ac.serial_number || '',
            year_manufactured: ac.year_manufactured || '',
            max_passengers: ac.max_passengers || 0,
            max_fuel_kg: ac.max_fuel_kg || 0,
            fuel_unit: ac.fuel_unit || 'kg',
            mtow_kg: ac.mtow_kg || 0,
            baseline_flight_hours: ac.baseline_flight_hours || 0,
            baseline_cycles: ac.baseline_cycles || 0,
            baseline_block_hours: ac.baseline_block_hours || 0,
            mlw_kg: ac.mlw_kg || 0,
            mzfw_kg: ac.mzfw_kg || 0,
            bow_kg: ac.bow_kg || 0,
            base_airport: ac.base_airport || '',
        });
    };

    const saveEditAircraft = async () => {
        if (!editDialog) return;
        if (!editForm.registration?.trim()) { toast.error('Matricula obligatoria'); return; }
        setSavingEdit(true);
        try {
            const payload = { ...editForm };
            // Normalizar matricula a mayusculas sin espacios
            payload.registration = String(payload.registration).toUpperCase().replace(/\s+/g, '');
            if (payload.base_airport) payload.base_airport = String(payload.base_airport).toUpperCase();
            // Convertir numeros
            ['max_passengers', 'max_fuel_kg', 'mtow_kg', 'mlw_kg', 'mzfw_kg', 'bow_kg', 'year_manufactured'].forEach(k => {
                if (payload[k] !== '' && payload[k] !== null && payload[k] !== undefined) {
                    payload[k] = Number(payload[k]) || 0;
                }
            });
            await axios.put(`${API}/aircraft/${editDialog.id}`, payload, { headers });
            toast.success('Aeronave actualizada');
            setEditDialog(null);
            fetchAircraft();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar aeronave');
        } finally {
            setSavingEdit(false);
        }
    };

    const fetchAircraft = async () => {
        try {
            const response = await axios.get(`${API}/aircraft`);
            setAircraft(response.data);
        } catch (error) {
            toast.error('Error al cargar flota');
        } finally {
            setLoading(false);
        }
    };

    const fetchHealth = async () => {
        try {
            const res = await axios.get(`${API}/fleet/health`);
            const map = {};
            (res.data.fleet || []).forEach(h => { map[h.aircraft_id] = h; });
            setHealthData(map);
        } catch { toast.error('No se pudo cargar el estado de salud de la flota'); }
    };

    const syncStatuses = async () => {
        try {
            const res = await axios.post(`${API}/fleet/sync-statuses`);
            toast.success(res.data.message);
            fetchAircraft();
            fetchHealth();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al sincronizar'); }
    };

    const handleCreateAircraft = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...newAircraft,
                year_manufactured: newAircraft.year_manufactured ? parseInt(newAircraft.year_manufactured) : null,
                max_passengers: parseInt(newAircraft.max_passengers) || 0,
                max_fuel_kg: parseFloat(newAircraft.max_fuel_kg) || 0,
                mtow_kg: parseFloat(newAircraft.mtow_kg) || null,
                mlw_kg: parseFloat(newAircraft.mlw_kg) || null,
                mzfw_kg: parseFloat(newAircraft.mzfw_kg) || null,
                bow_kg: parseFloat(newAircraft.bow_kg) || null,
                total_flight_hours: parseFloat(newAircraft.total_flight_hours) || 0,
                total_block_hours: parseFloat(newAircraft.total_block_hours) || 0,
                total_cycles: parseInt(newAircraft.total_cycles) || 0,
                // Auditoria 7 - Item 6: baseline
                baseline_flight_hours: parseFloat(newAircraft.baseline_flight_hours) || 0,
                baseline_block_hours: parseFloat(newAircraft.baseline_block_hours) || 0,
                baseline_cycles: parseInt(newAircraft.baseline_cycles) || 0,
            };
            if (!data.is_used_aircraft) {
                delete data.check_history;
            }
            const res = await axios.post(`${API}/aircraft`, data);
            if (pendingTcpMin !== '' && newAircraft.aircraft_type) {
                try { await axios.put(`${API}/fleet/tcp-minimums/${encodeURIComponent(newAircraft.aircraft_type.trim().toUpperCase())}`, { min_tcp: Number(pendingTcpMin) }); }
                catch (e2) { toast.error(e2.response?.data?.detail || 'No se pudo guardar el mínimo de TCP'); }
                setPendingTcpMin('');
            }
            // Apply seat config from manual fields
            const colsL = (newAircraft.seat_cols_left || '').split(',').map(s => s.trim()).filter(Boolean);
            const colsR = (newAircraft.seat_cols_right || '').split(',').map(s => s.trim()).filter(Boolean);
            const totalRows = newAircraft.seat_total_rows || 0;
            if (colsL.length > 0 && colsR.length > 0 && totalRows > 0 && res.data.id) {
                const exitRows = (newAircraft.seat_exit_rows || '').split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                const sectionParts = (newAircraft.seat_sections || '').split(',').map(s => s.trim()).filter(Boolean);
                const sections = sectionParts.map(sp => {
                    const [name, range] = sp.split(':');
                    if (!range) return null;
                    const [start, end] = range.split('-').map(Number);
                    return name && start && end ? { name: name.trim(), start_row: start, end_row: end } : null;
                }).filter(Boolean);
                try {
                    await axios.put(`${API}/aircraft/${res.data.id}/seat-config`, {
                        columns_left: colsL, columns_right: colsR, total_rows: totalRows,
                        sections, exit_rows: exitRows, blocked_seats: [],
                    });
                } catch { toast.warning('Aeronave creada, pero no se pudo guardar la configuración de asientos. Edítala desde la ficha.'); }
            }
            const msg = res.data.mx_programs_initialized
                ? 'Aeronave agregada con historial — programas de mantenimiento inicializados'
                : 'Aeronave agregada exitosamente';
            toast.success(msg);
            setDialogOpen(false);
            fetchAircraft();
            fetchHealth();
            setNewAircraft({
                registration: '', aircraft_type: '', model: '',
                manufacturer: '', serial_number: '', year_manufactured: '',
                max_passengers: 0, max_fuel_kg: 0, fuel_unit: 'kg', mtow_kg: 0,
                baseline_flight_hours: 0, baseline_cycles: 0, baseline_block_hours: 0,
                mlw_kg: 0, mzfw_kg: 0, bow_kg: 0, base_airport: '', status: 'operational',
                is_used_aircraft: false, total_flight_hours: 0, total_block_hours: 0, total_cycles: 0,
                last_weight_date: '',
                check_history: {
                    A: { last_date: '', last_hours: '', last_cycles: '' },
                    B: { last_date: '', last_hours: '', last_cycles: '' },
                    C: { last_date: '', last_hours: '', last_cycles: '' },
                    D: { last_date: '', last_hours: '', last_cycles: '' },
                }
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear aeronave');
        }
    };

    const getStatusBadge = (status) => {
        const opStatus = status || 'active';
        const configs = {
            operational: { label: 'Operativa', className: 'status-success' },
            active: { label: 'Activa', className: 'status-success' },
            maintenance: { label: 'En Mtto', className: 'status-warning' },
            aog: { label: 'AOG', className: 'status-danger' },
            storage: { label: 'Almacenada', className: 'bg-muted text-muted-foreground' },
            reserved: { label: 'Reservada', className: 'status-info' }
        };
        const config = configs[opStatus] || { label: opStatus, className: '' };
        return <Badge className={config.className}>{config.label}</Badge>;
    };

    const changeOperationalStatus = async () => {
        if (!statusDialog || !newStatus) return;
        try {
            await axios.patch(`${API}/aircraft/${statusDialog.id}/operational-status`, {
                operational_status: newStatus,
                reason: statusReason
            });
            toast.success(`Estado cambiado a ${({ active: 'Activa', maintenance: 'En mantenimiento', aog: 'AOG', storage: 'Almacenada' })[newStatus] || newStatus}`);
            setStatusDialog(null);
            setNewStatus('');
            setStatusReason('');
            fetchAircraft();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cambiar estado');
        }
    };

    const canChangeStatus = hasRole(['admin', 'maintenance', 'ccod']);

    const recalculateHours = async () => {
        try {
            await axios.post(`${API}/fleet/recalculate-hours`);
            toast.success('Horas de flota recalculadas');
            fetchAircraft();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const openAircraftReport = async (ac) => {
        setReportDialog(ac);
        setReportLoading(true);
        try {
            const res = await axios.get(`${API}/fleet/report/${ac.id}?start_date=2024-01-01&end_date=2026-12-31`);
            setReportData(res.data);
        } catch { toast.error('Error al cargar reporte'); }
        finally { setReportLoading(false); }
    };

    const deactivateAircraft = async (ac) => {
        if (!window.confirm(`Dar de baja a ${ac.registration}? (El historial se mantiene)`)) return;
        try {
            const res = await axios.delete(`${API}/aircraft/${ac.id}`, { headers });
            toast.success(res.data.message);
            fetchAircraft();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const filteredAircraft = aircraft.filter(ac => {
        const matchesSearch = ac.registration.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ac.aircraft_type.toLowerCase().includes(searchTerm.toLowerCase());
        const acStatus = ac.operational_status || ac.status || 'active';
        const matchesStatus = statusFilter === 'all' || acStatus === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const canManageFleet = hasRole(['admin', 'maintenance']);

    if (loading) {
        return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-muted rounded-lg skeleton-pulse" />
            ))}
        </div>;
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="fleet-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        Gestión de Flota
                    </h1>
                    <p className="text-muted-foreground">
                        {aircraft.length} aeronaves registradas
                    </p>
                </div>
                {canManageFleet && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={syncStatuses} data-testid="sync-fleet-btn">
                            <RefreshCw className="w-4 h-4 mr-2" />Sincronizar
                        </Button>
                        <Button variant="outline" onClick={recalculateHours} data-testid="recalculate-fleet-btn">
                            <Clock className="w-4 h-4 mr-2" />Recalcular Horas
                        </Button>
                        <Dialog open={dialogOpen} onOpenChange={o => { setDialogOpen(o); if (!o) setPendingTcpMin(''); }}>
                            <DialogTrigger asChild>
                                <Button className="btn-primary" data-testid="new-aircraft-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Nueva Aeronave
                                </Button>
                            </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">
                                    Agregar Aeronave
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateAircraft} className="space-y-4">
                                {/* Identificación */}
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Identificación</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Matrícula *</Label>
                                            <Input
                                                placeholder="HK-1234, N12345, EC-ABC..."
                                                value={newAircraft.registration}
                                                onChange={(e) => setNewAircraft({...newAircraft, registration: e.target.value.toUpperCase()})}
                                                required
                                                data-testid="aircraft-registration"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Número de Serie (MSN)</Label>
                                            <Input
                                                placeholder="12345"
                                                value={newAircraft.serial_number}
                                                onChange={(e) => setNewAircraft({...newAircraft, serial_number: e.target.value})}
                                                data-testid="aircraft-serial"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Tipo y Modelo */}
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Tipo y Modelo</p>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Fabricante *</Label>
                                            <Input
                                                placeholder="Airbus, Boeing, Embraer, ATR..."
                                                value={newAircraft.manufacturer}
                                                onChange={(e) => setNewAircraft({...newAircraft, manufacturer: e.target.value})}
                                                required
                                                data-testid="aircraft-manufacturer"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Tipo ICAO *</Label>
                                            <Input
                                                placeholder="A320, B738, E190, AT72..."
                                                value={newAircraft.aircraft_type}
                                                onChange={(e) => {
                                                    const t = e.target.value.toUpperCase();
                                                    const std = standardCapacity(t);
                                                    const prevStd = standardCapacity(newAircraft.aircraft_type);
                                                    const pax = newAircraft.max_passengers;
                                                    const preload = std && (!pax || Number(pax) === 0 || Number(pax) === prevStd);
                                                    setNewAircraft({...newAircraft, aircraft_type: t, ...(preload ? { max_passengers: std } : {})});
                                                }}
                                                required
                                                data-testid="aircraft-type"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Modelo</Label>
                                            <Input
                                                placeholder="A320-200, 737-800, E190-E2..."
                                                value={newAircraft.model}
                                                onChange={(e) => setNewAircraft({...newAircraft, model: e.target.value})}
                                                data-testid="aircraft-model"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Año de Fabricación</Label>
                                            <Input
                                                type="number"
                                                placeholder="2020"
                                                min="1950"
                                                max={new Date().getFullYear()}
                                                value={newAircraft.year_manufactured}
                                                onChange={(e) => setNewAircraft({...newAircraft, year_manufactured: e.target.value})}
                                                data-testid="aircraft-year"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Base de Operaciones (ICAO)</Label>
                                            <Input
                                                placeholder="SKBO, SKCL, KMIA..."
                                                value={newAircraft.base_airport}
                                                onChange={(e) => setNewAircraft({...newAircraft, base_airport: e.target.value.toUpperCase()})}
                                                maxLength={4}
                                                data-testid="aircraft-base"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Capacidad */}
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Capacidad</p>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Máx. Pasajeros *</Label>
                                            <Input
                                                type="number"
                                                placeholder="180"
                                                value={newAircraft.max_passengers}
                                                onChange={(e) => setNewAircraft({...newAircraft, max_passengers: e.target.value})}
                                                required
                                                data-testid="aircraft-passengers"
                                            />
                                            {standardCapacity(newAircraft.aircraft_type) > 0 && (
                                                <span className="text-[10px] text-muted-foreground" data-testid="aircraft-standard-capacity">Estándar {newAircraft.aircraft_type}: {standardCapacity(newAircraft.aircraft_type)} asientos (editable para esta matrícula)</span>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Máx. Combustible ({newAircraft.fuel_unit || 'kg'}) *</Label>
                                            <Input
                                                type="number"
                                                placeholder="24000"
                                                value={newAircraft.max_fuel_kg}
                                                onChange={(e) => setNewAircraft({...newAircraft, max_fuel_kg: e.target.value})}
                                                required
                                                data-testid="aircraft-fuel"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Unidad de Combustible *</Label>
                                            <Select
                                                value={newAircraft.fuel_unit || 'kg'}
                                                onValueChange={(v) => setNewAircraft({...newAircraft, fuel_unit: v})}
                                            >
                                                <SelectTrigger data-testid="aircraft-fuel-unit">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                                                    <SelectItem value="L">Litros (L)</SelectItem>
                                                    <SelectItem value="lbs">Libras (lbs)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <TcpMinimumField aircraftType={newAircraft.aircraft_type} maxPassengers={newAircraft.max_passengers} deferred value={pendingTcpMin} onDeferredChange={setPendingTcpMin} />
                                </div>

                                {/* Pesos */}
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Pesos (kg) - Opcional</p>
                                    <div className="grid grid-cols-4 gap-3">
                                        <div className="space-y-2">
                                            <Label className="text-xs">MTOW</Label>
                                            <Input
                                                type="number"
                                                placeholder="78000"
                                                value={newAircraft.mtow_kg || ''}
                                                onChange={(e) => setNewAircraft({...newAircraft, mtow_kg: e.target.value})}
                                                data-testid="aircraft-mtow"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs">MLW</Label>
                                            <Input
                                                type="number"
                                                placeholder="66000"
                                                value={newAircraft.mlw_kg || ''}
                                                onChange={(e) => setNewAircraft({...newAircraft, mlw_kg: e.target.value})}
                                                data-testid="aircraft-mlw"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs">MZFW</Label>
                                            <Input
                                                type="number"
                                                placeholder="62000"
                                                value={newAircraft.mzfw_kg || ''}
                                                onChange={(e) => setNewAircraft({...newAircraft, mzfw_kg: e.target.value})}
                                                data-testid="aircraft-mzfw"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs">BOW</Label>
                                            <Input
                                                type="number"
                                                placeholder="42000"
                                                value={newAircraft.bow_kg || ''}
                                                onChange={(e) => setNewAircraft({...newAircraft, bow_kg: e.target.value})}
                                                data-testid="aircraft-bow"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        MTOW: Peso Max. Despegue | MLW: Peso Max. Aterrizaje | MZFW: Peso Max. Sin Combustible | BOW: Peso Operativo Vacio
                                    </p>
                                </div>

                                {/* Used Aircraft History */}
                                <div className="space-y-3 border-t border-border/50 pt-4">
                                    <div className="flex items-center gap-3">
                                        <Switch checked={newAircraft.is_used_aircraft} onCheckedChange={(v) => setNewAircraft({...newAircraft, is_used_aircraft: v})} data-testid="aircraft-is-used" />
                                        <div>
                                            <Label className="font-semibold">Aeronave Usada / Con Historial</Label>
                                            <p className="text-xs text-muted-foreground">Activa si la aeronave trae horas, ciclos y checks acumulados</p>
                                        </div>
                                    </div>

                                    {newAircraft.is_used_aircraft && (
                                        <div className="space-y-4 p-3 bg-muted/20 rounded-lg border border-border/50">
                                            {/* Auditoria 7 - Item 6: Baseline (historial previo a AeroOps) */}
                                            <div>
                                                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Historial base (previo a AeroOps)</p>
                                                <p className="text-[10px] text-muted-foreground mb-2">
                                                    Estas horas/ciclos son la lectura de tablilla al momento de dar de alta. AeroOps SUMA sus propios vuelos encima; nunca reemplaza este baseline.
                                                </p>
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div className="space-y-1.5">
                                                        <Label className="text-xs">Horas de Vuelo base</Label>
                                                        <Input type="number" step="0.1" placeholder="12500.5" value={newAircraft.baseline_flight_hours || ''} onChange={(e) => setNewAircraft({...newAircraft, baseline_flight_hours: parseFloat(e.target.value) || 0})} data-testid="aircraft-baseline-fh" />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-xs">Horas de Block base</Label>
                                                        <Input type="number" step="0.1" placeholder="13200.0" value={newAircraft.baseline_block_hours || ''} onChange={(e) => setNewAircraft({...newAircraft, baseline_block_hours: parseFloat(e.target.value) || 0})} data-testid="aircraft-baseline-bh" />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-xs">Ciclos base</Label>
                                                        <Input type="number" placeholder="8500" value={newAircraft.baseline_cycles || ''} onChange={(e) => setNewAircraft({...newAircraft, baseline_cycles: parseInt(e.target.value) || 0})} data-testid="aircraft-baseline-cycles" />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Last Checks */}
                                            <div>
                                                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Ultimo Check Realizado</p>
                                                <p className="text-xs text-muted-foreground mb-3">Ingresa la fecha y las horas/ciclos al momento de cada ultimo check. El sistema calculara automaticamente cuando toca el proximo.</p>
                                                <div className="space-y-3">
                                                    {['A', 'B', 'C', 'D'].map(ct => (
                                                        <div key={ct} className="grid grid-cols-4 gap-2 items-end">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-7 h-7 rounded flex items-center justify-center font-bold text-xs ${ct === 'A' ? 'bg-blue-500/20 text-blue-400' : ct === 'B' ? 'bg-violet-500/20 text-violet-400' : ct === 'C' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{ct}</div>
                                                                <div className="space-y-0.5">
                                                                    <Label className="text-[10px]">Fecha</Label>
                                                                    <Input type="date" className="h-8 text-xs" value={newAircraft.check_history[ct].last_date} onChange={(e) => setNewAircraft({...newAircraft, check_history: {...newAircraft.check_history, [ct]: {...newAircraft.check_history[ct], last_date: e.target.value}}})} data-testid={`aircraft-check-${ct}-date`} />
                                                                </div>
                                                            </div>
                                                            <div className="space-y-0.5">
                                                                <Label className="text-[10px]">Horas al Check</Label>
                                                                <Input type="number" step="0.1" className="h-8 text-xs font-mono" placeholder="0" value={newAircraft.check_history[ct].last_hours} onChange={(e) => setNewAircraft({...newAircraft, check_history: {...newAircraft.check_history, [ct]: {...newAircraft.check_history[ct], last_hours: parseFloat(e.target.value) || 0}}})} data-testid={`aircraft-check-${ct}-hours`} />
                                                            </div>
                                                            <div className="space-y-0.5">
                                                                <Label className="text-[10px]">Ciclos al Check</Label>
                                                                <Input type="number" className="h-8 text-xs font-mono" placeholder="0" value={newAircraft.check_history[ct].last_cycles} onChange={(e) => setNewAircraft({...newAircraft, check_history: {...newAircraft.check_history, [ct]: {...newAircraft.check_history[ct], last_cycles: parseInt(e.target.value) || 0}}})} data-testid={`aircraft-check-${ct}-cycles`} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Seat Configuration */}
                                <div className="space-y-3 border-t border-border/50 pt-4">
                                    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Configuracion de Asientos</p>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Plantilla rapida (opcional)</Label>
                                        <Select value={seatTemplate} onValueChange={(v) => {
                                            setSeatTemplate(v);
                                            if (v && v !== 'none') {
                                                const presets = {
                                                    atr72: { seat_cols_left: 'A,C', seat_cols_right: 'D,F', seat_total_rows: 18, seat_sections: 'Cabina A:1-6,Cabina B:7-13,Cabina C:14-18', seat_exit_rows: '1,14' },
                                                    '737-800': { seat_cols_left: 'A,B,C', seat_cols_right: 'D,E,F', seat_total_rows: 33, seat_sections: 'Business:1-4,Economy:5-33', seat_exit_rows: '1,12,13' },
                                                    saab340: { seat_cols_left: 'A', seat_cols_right: 'C,D', seat_total_rows: 11, seat_sections: 'Cabina:1-11', seat_exit_rows: '1' },
                                                    caravan208: { seat_cols_left: 'A', seat_cols_right: 'B', seat_total_rows: 6, seat_sections: 'Cabina:1-6', seat_exit_rows: '1' },
                                                    embraer190: { seat_cols_left: 'A,C', seat_cols_right: 'D,F', seat_total_rows: 25, seat_sections: 'Business:1-4,Economy:5-25', seat_exit_rows: '1,11' },
                                                };
                                                const p = presets[v];
                                                if (p) setNewAircraft(prev => ({ ...prev, ...p }));
                                            }
                                        }}>
                                            <SelectTrigger data-testid="seat-template-select"><SelectValue placeholder="Seleccionar plantilla..." /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Manual / Sin plantilla</SelectItem>
                                                <SelectItem value="atr72">ATR 72 — 2+2 x 18</SelectItem>
                                                <SelectItem value="737-800">737-800 — 3+3 x 33</SelectItem>
                                                <SelectItem value="saab340">Saab 340 — 1+2 x 11</SelectItem>
                                                <SelectItem value="caravan208">Caravan — 1+1 x 6</SelectItem>
                                                <SelectItem value="embraer190">E190 — 2+2 x 25</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1"><Label className="text-xs">Columnas Izq.</Label><Input placeholder="A,C" value={newAircraft.seat_cols_left || ''} onChange={e => setNewAircraft({...newAircraft, seat_cols_left: e.target.value.toUpperCase()})} data-testid="seat-cols-left" /><p className="text-[9px] text-muted-foreground">Ej: A,B,C</p></div>
                                        <div className="space-y-1"><Label className="text-xs">Columnas Der.</Label><Input placeholder="D,F" value={newAircraft.seat_cols_right || ''} onChange={e => setNewAircraft({...newAircraft, seat_cols_right: e.target.value.toUpperCase()})} data-testid="seat-cols-right" /></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1"><Label className="text-xs">Total Filas</Label><Input type="number" placeholder="18" value={newAircraft.seat_total_rows || ''} onChange={e => setNewAircraft({...newAircraft, seat_total_rows: parseInt(e.target.value) || 0})} data-testid="seat-total-rows" /></div>
                                        <div className="space-y-1"><Label className="text-xs">Filas Emergencia</Label><Input placeholder="1,14" value={newAircraft.seat_exit_rows || ''} onChange={e => setNewAircraft({...newAircraft, seat_exit_rows: e.target.value})} data-testid="seat-exit-rows" /></div>
                                    </div>
                                    <div className="space-y-1"><Label className="text-xs">Secciones Cabina</Label><Input placeholder="Cabina A:1-6,Cabina B:7-13" value={newAircraft.seat_sections || ''} onChange={e => setNewAircraft({...newAircraft, seat_sections: e.target.value})} data-testid="seat-sections" /><p className="text-[9px] text-muted-foreground">Nombre:inicio-fin separados por coma</p></div>
                                </div>

                                <AircraftCostPreview open={dialogOpen} storage={newAircraft.operational_status === 'storage'} />
                                <Button type="submit" className="w-full btn-primary" data-testid="create-aircraft-submit">
                                    Agregar Aeronave
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por matrícula o tipo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="search-aircraft"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-48" data-testid="fleet-status-filter">
                        <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="active">Activa</SelectItem>
                        <SelectItem value="operational">Operativa</SelectItem>
                        <SelectItem value="maintenance">En Mantenimiento</SelectItem>
                        <SelectItem value="aog">AOG</SelectItem>
                        <SelectItem value="storage">Almacenada</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Fleet Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <PlaneTakeoff className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">
                                {aircraft.filter(a => (a.operational_status || a.status || 'active') === 'active' || (a.operational_status || a.status) === 'operational').length}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Activas</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <Wrench className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">
                                {aircraft.filter(a => (a.operational_status || a.status) === 'maintenance').length}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">En Mtto</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">
                                {aircraft.filter(a => (a.operational_status || a.status) === 'aog').length}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">AOG</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <PlaneTakeoff className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <p className="font-mono text-2xl font-bold">{aircraft.length}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Aircraft Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAircraft.length === 0 ? (
                    <Card className="card-aviation col-span-full">
                        <CardContent className="py-12 text-center text-muted-foreground">
                            <PlaneTakeoff className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No se encontraron aeronaves</p>
                        </CardContent>
                    </Card>
                ) : (
                    filteredAircraft.map((ac) => (
                        <Card key={ac.id} className="card-aviation aircraft-card" data-testid={`aircraft-card-${ac.registration}`}>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="font-mono text-2xl text-primary">
                                        {ac.registration}
                                    </CardTitle>
                                    <div className="flex items-center gap-1">
                                        {getStatusBadge(ac.operational_status || ac.status)}
                                        {canChangeStatus && (
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEditDialog(ac)} data-testid={`edit-aircraft-${ac.registration}`} title="Editar aeronave">
                                                <Pencil className="w-3.5 h-3.5 text-primary" />
                                            </Button>
                                        )}
                                        {canChangeStatus && (
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setStatusDialog(ac); setNewStatus(ac.operational_status || ac.status || 'active'); setStatusReason(''); }} data-testid={`change-status-${ac.registration}`}>
                                                <Settings2 className="w-3.5 h-3.5 text-muted-foreground" />
                                            </Button>
                                        )}
                                        {canChangeStatus && (
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openSeatConfigDialog(ac)} data-testid={`seat-config-${ac.registration}`} title="Configurar mapa de asientos">
                                                <LayoutGrid className="w-3.5 h-3.5 text-amber-500" />
                                            </Button>
                                        )}
                                        {canChangeStatus && (
                                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deactivateAircraft(ac)} data-testid={`deactivate-${ac.registration}`}>
                                                <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                            </Button>
                                        )}
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setAuditAircraft(ac)} data-testid={`audit-aircraft-${ac.registration}`} title="Historial de cambios">
                                            <History className="w-3.5 h-3.5 text-muted-foreground" />
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {ac.manufacturer} {ac.model || ac.aircraft_type}
                                </p>
                                {ac.serial_number && (
                                    <p className="text-xs text-muted-foreground font-mono">
                                        MSN: {ac.serial_number}
                                    </p>
                                )}
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-muted-foreground" />
                                        <span className="font-mono">{ac.max_passengers} PAX</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Fuel className="w-4 h-4 text-muted-foreground" />
                                        <span className="font-mono">{ac.max_fuel_kg?.toLocaleString() || 0} {ac.fuel_unit || 'kg'}</span>
                                    </div>
                                </div>
                                {(ac.year_manufactured || ac.base_airport) && (
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {ac.year_manufactured && (
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                <span className="font-mono">{ac.year_manufactured}</span>
                                            </div>
                                        )}
                                        {ac.base_airport && (
                                            <div className="flex items-center gap-2">
                                                <PlaneTakeoff className="w-4 h-4 text-muted-foreground" />
                                                <span className="font-mono">{ac.base_airport}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="pt-2 border-t border-border/50 text-xs text-muted-foreground space-y-1">
                                    {/* Health Status */}
                                    {(() => {
                                        const h = healthData[ac.id];
                                        if (!h) return null;
                                        const defOverdue = h.deferrals?.overdue || 0;
                                        const defActive = h.deferrals?.active || 0;
                                        const mxOverdue = h.maintenance?.overdue_checks || 0;
                                        const mxWarning = h.maintenance?.warning_checks || 0;
                                        const baseMx = h.maintenance?.active_base_orders || 0;
                                        const hasIssues = defOverdue > 0 || defActive > 0 || mxOverdue > 0 || mxWarning > 0 || baseMx > 0;
                                        if (!hasIssues) return (
                                            <div className="flex items-center gap-1 text-emerald-400 mb-1" data-testid={`health-ok-${ac.registration}`}>
                                                <Shield className="w-3 h-3" /><span className="text-[10px] font-bold uppercase">Limpia — Sin Diferidos ni Alertas</span>
                                            </div>
                                        );
                                        return (
                                            <div className="space-y-1 mb-1" data-testid={`health-issues-${ac.registration}`}>
                                                {defOverdue > 0 && (
                                                    <div className="flex items-center gap-1 text-red-400 bg-red-500/5 rounded px-1.5 py-0.5">
                                                        <AlertTriangle className="w-3 h-3" /><span className="text-[10px] font-bold">{defOverdue} diferido(s) VENCIDO(S)</span>
                                                    </div>
                                                )}
                                                {defActive > 0 && defOverdue === 0 && (
                                                    <div className="flex items-center gap-1 text-amber-400">
                                                        <AlertTriangle className="w-3 h-3" /><span className="text-[10px]">{defActive} diferido(s) activo(s)</span>
                                                    </div>
                                                )}
                                                {mxOverdue > 0 && (
                                                    <div className="flex items-center gap-1 text-red-400 bg-red-500/5 rounded px-1.5 py-0.5">
                                                        <Wrench className="w-3 h-3" /><span className="text-[10px] font-bold">{mxOverdue} check(s) VENCIDO(S)</span>
                                                    </div>
                                                )}
                                                {mxWarning > 0 && (
                                                    <div className="flex items-center gap-1 text-amber-400">
                                                        <Wrench className="w-3 h-3" /><span className="text-[10px]">{mxWarning} check(s) proximamente</span>
                                                    </div>
                                                )}
                                                {baseMx > 0 && (
                                                    <div className="flex items-center gap-1 text-violet-400">
                                                        <Wrench className="w-3 h-3" /><span className="text-[10px]">{baseMx} orden(es) base activa(s)</span>
                                                    </div>
                                                )}
                                                {h.needs_attention && h.grounding_reasons?.length > 0 && (
                                                    <div className="text-[9px] text-red-400 italic">{h.grounding_reasons.join(' | ')}</div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    <div className="flex justify-between">
                                        <span>H. Vuelo:</span>
                                        <span className="font-mono font-semibold text-foreground" data-testid={`flight-hours-${ac.registration}`}>{ac.total_flight_hours?.toFixed(1) || '0.0'} hrs</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>H. Block:</span>
                                        <span className="font-mono font-semibold text-foreground" data-testid={`block-hours-${ac.registration}`}>{ac.total_block_hours?.toFixed(1) || '0.0'} hrs</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Ciclos:</span>
                                        <span className="font-mono font-semibold text-foreground" data-testid={`cycles-${ac.registration}`}>{ac.total_cycles?.toLocaleString() || 0}</span>
                                    </div>
                                    {ac.mtow_kg && (
                                        <div className="flex justify-between">
                                            <span>MTOW:</span>
                                            <span className="font-mono">{ac.mtow_kg?.toLocaleString()} kg</span>
                                        </div>
                                    )}
                                    <Button variant="ghost" size="sm" className="w-full mt-1 h-7 text-xs" onClick={() => openAircraftReport(ac)} data-testid={`report-btn-${ac.registration}`}>
                                        <BarChart3 className="w-3 h-3 mr-1" />Ver Reporte
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Operational Status Change Dialog */}
            <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <Settings2 className="w-5 h-5 text-primary" />
                            Estado Operacional — {statusDialog?.registration}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Nuevo Estado</Label>
                            <Select value={newStatus} onValueChange={setNewStatus}>
                                <SelectTrigger data-testid="new-op-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Activa</SelectItem>
                                    <SelectItem value="maintenance">En Mantenimiento</SelectItem>
                                    <SelectItem value="aog">AOG (Aircraft on Ground)</SelectItem>
                                    <SelectItem value="storage">Almacenada</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Razon del cambio</Label>
                            <Textarea value={statusReason} onChange={e => setStatusReason(e.target.value)} placeholder="Motivo del cambio de estado..." rows={2} data-testid="status-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancelar</Button>
                        <Button onClick={changeOperationalStatus} className="btn-primary" data-testid="submit-status-change">Cambiar Estado</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Aircraft Report Dialog */}
            <Dialog open={!!reportDialog} onOpenChange={() => { setReportDialog(null); setReportData(null); }}>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Reporte — {reportDialog?.registration}
                        </DialogTitle>
                    </DialogHeader>
                    {reportLoading && <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-primary" /></div>}
                    {reportData && !reportLoading && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-muted/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold font-mono" data-testid="report-cycles">{reportData.summary.total_cycles}</p>
                                    <p className="text-[10px] text-muted-foreground uppercase">Ciclos</p>
                                </div>
                                <div className="bg-muted/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold font-mono" data-testid="report-flight-hours">{reportData.summary.total_flight_hours}h</p>
                                    <p className="text-[10px] text-muted-foreground uppercase">H. Vuelo</p>
                                </div>
                                <div className="bg-muted/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold font-mono" data-testid="report-block-hours">{reportData.summary.total_block_hours}h</p>
                                    <p className="text-[10px] text-muted-foreground uppercase">H. Block</p>
                                </div>
                            </div>
                            {reportData.top_routes.length > 0 && (
                                <div>
                                    <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Rutas Principales</p>
                                    <div className="flex flex-wrap gap-2">
                                        {reportData.top_routes.map(r => (
                                            <Badge key={r.route} variant="outline" className="font-mono text-xs">{r.route} ({r.flights})</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {reportData.flights.length > 0 && (
                                <div>
                                    <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Historial de Vuelos</p>
                                    <div className="overflow-x-auto">
                                        <table className="table-aviation w-full text-xs">
                                            <thead><tr><th>Vuelo</th><th>Ruta</th><th>Fecha</th><th>Estado</th><th>Block</th><th>Vuelo</th></tr></thead>
                                            <tbody>
                                                {reportData.flights.map((f, i) => (
                                                    <tr key={i}>
                                                        <td className="font-mono font-bold text-primary">{f.flight_number}</td>
                                                        <td className="font-mono">{f.origin}-{f.destination}</td>
                                                        <td className="font-mono">{f.date}</td>
                                                        <td><Badge variant="outline" className="text-[10px]">{f.status}</Badge></td>
                                                        <td className="font-mono">{f.block_time ? `${Math.floor(f.block_time/60)}:${String(f.block_time%60).padStart(2,'0')}` : '-'}</td>
                                                        <td className="font-mono">{f.flight_time ? `${Math.floor(f.flight_time/60)}:${String(f.flight_time%60).padStart(2,'0')}` : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* FASE 2 UI: Historial de auditoria */}
            <AuditHistoryModal
                open={!!auditAircraft}
                onOpenChange={(o) => { if (!o) setAuditAircraft(null); }}
                collection="aircraft"
                entityId={auditAircraft?.id}
                entityLabel={auditAircraft ? `${auditAircraft.registration} — ${auditAircraft.manufacturer || ''} ${auditAircraft.model || auditAircraft.aircraft_type || ''}`.trim() : ''}
            />

            {/* Editar Aeronave completo (Audit 3 follow-up) */}
            <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
                <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="edit-aircraft-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2">
                            <Pencil className="w-5 h-5 text-primary" />
                            Editar Aeronave — {editDialog?.registration}
                        </DialogTitle>
                        <DialogDescription>
                            Actualiza los datos maestros de la aeronave. El estado operacional y la configuración de asientos se editan por separado.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 py-2">
                        <div className="space-y-1.5">
                            <Label>Matrícula *</Label>
                            <Input value={editForm.registration || ''} onChange={e => setEditForm({...editForm, registration: e.target.value})} data-testid="edit-registration" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Fabricante</Label>
                            <Input value={editForm.manufacturer || ''} onChange={e => setEditForm({...editForm, manufacturer: e.target.value})} placeholder="Boeing, Airbus, ATR..." data-testid="edit-manufacturer" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Modelo</Label>
                            <Input value={editForm.model || ''} onChange={e => setEditForm({...editForm, model: e.target.value})} placeholder="737-800, A320, ATR 72-600..." data-testid="edit-model" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Tipo (ICAO)</Label>
                            <Input value={editForm.aircraft_type || ''} onChange={e => setEditForm({...editForm, aircraft_type: e.target.value})} placeholder="B738, A320, AT72" data-testid="edit-type" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>MSN (Serial)</Label>
                            <Input value={editForm.serial_number || ''} onChange={e => setEditForm({...editForm, serial_number: e.target.value})} placeholder="MSN-12345" data-testid="edit-msn" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Año</Label>
                            <Input type="number" min="1950" max="2035" value={editForm.year_manufactured || ''} onChange={e => setEditForm({...editForm, year_manufactured: e.target.value})} data-testid="edit-year" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Base (ICAO)</Label>
                            <Input value={editForm.base_airport || ''} onChange={e => setEditForm({...editForm, base_airport: e.target.value})} placeholder="SKBO, MGGT..." data-testid="edit-base" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>PAX (capacidad)</Label>
                            <Input type="number" min="0" value={editForm.max_passengers || 0} onChange={e => setEditForm({...editForm, max_passengers: e.target.value})} data-testid="edit-pax" />
                        </div>
                        <TcpMinimumField aircraftType={editForm.aircraft_type} maxPassengers={editForm.max_passengers} />
                        <div className="space-y-1.5">
                            <Label>Combustible máx ({editForm.fuel_unit || 'kg'})</Label>
                            <Input type="number" min="0" value={editForm.max_fuel_kg || 0} onChange={e => setEditForm({...editForm, max_fuel_kg: e.target.value})} data-testid="edit-fuel" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Unidad de Combustible</Label>
                            <Select value={editForm.fuel_unit || 'kg'} onValueChange={(v) => setEditForm({...editForm, fuel_unit: v})}>
                                <SelectTrigger data-testid="edit-fuel-unit">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                                    <SelectItem value="L">Litros (L)</SelectItem>
                                    <SelectItem value="lbs">Libras (lbs)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>MTOW (kg)</Label>
                            <Input type="number" min="0" value={editForm.mtow_kg || 0} onChange={e => setEditForm({...editForm, mtow_kg: e.target.value})} data-testid="edit-mtow" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>MLW (kg)</Label>
                            <Input type="number" min="0" value={editForm.mlw_kg || 0} onChange={e => setEditForm({...editForm, mlw_kg: e.target.value})} data-testid="edit-mlw" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>MZFW (kg)</Label>
                            <Input type="number" min="0" value={editForm.mzfw_kg || 0} onChange={e => setEditForm({...editForm, mzfw_kg: e.target.value})} data-testid="edit-mzfw" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>BOW (kg)</Label>
                            <Input type="number" min="0" value={editForm.bow_kg || 0} onChange={e => setEditForm({...editForm, bow_kg: e.target.value})} data-testid="edit-bow" />
                        </div>
                    </div>

                    {/* Auditoria 7 - Items 6/21: Baseline y correccion manual de totales */}
                    <div className="pt-3 border-t border-border/40 space-y-3">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Historial (baseline previo a AeroOps)</p>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Horas de Vuelo base</Label>
                                <Input type="number" step="0.1" min="0" value={editForm.baseline_flight_hours ?? 0} onChange={e => setEditForm({...editForm, baseline_flight_hours: e.target.value})} data-testid="edit-baseline-fh" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Horas de Block base</Label>
                                <Input type="number" step="0.1" min="0" value={editForm.baseline_block_hours ?? 0} onChange={e => setEditForm({...editForm, baseline_block_hours: e.target.value})} data-testid="edit-baseline-bh" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Ciclos base</Label>
                                <Input type="number" min="0" value={editForm.baseline_cycles ?? 0} onChange={e => setEditForm({...editForm, baseline_cycles: e.target.value})} data-testid="edit-baseline-cycles" />
                            </div>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Total actual: <span className="font-mono">{editDialog?.total_flight_hours || 0}h · {editDialog?.total_cycles || 0} ciclos</span>
                            (baseline + {editDialog?.internal_flight_hours || 0}h internos)
                        </p>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialog(null)}>Cancelar</Button>
                        <Button onClick={saveEditAircraft} disabled={savingEdit} className="btn-primary" data-testid="save-edit-aircraft">
                            {savingEdit ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
                            Guardar cambios
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bug fix: dialogo de configuracion de mapa de asientos */}
            <Dialog open={!!seatConfigDialog} onOpenChange={(o) => { if (!o) setSeatConfigDialog(null); }}>
                <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto" data-testid="seat-config-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2">
                            <LayoutGrid className="w-5 h-5 text-amber-500" />
                            Mapa de Asientos — {seatConfigDialog?.registration}
                        </DialogTitle>
                        <DialogDescription>
                            Define la distribución de asientos de la aeronave. Se usa en Mostrador para asignar y visualizar asientos.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-3">
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
                            Configura la distribucion de asientos de esta aeronave. Puedes elegir una plantilla preconfigurada o definir columnas y filas manualmente. Este mapa se usa en Mostrador para asignar asientos a los pasajeros.
                        </div>
                        <div className="space-y-1.5">
                            <Label>Plantilla preconfigurada</Label>
                            <Select value={seatConfig.template_key || 'none'} onValueChange={(v) => v === 'none' ? setSeatConfig({...seatConfig, template_key: ''}) : applySeatTemplate(v)}>
                                <SelectTrigger data-testid="seat-template-select"><SelectValue placeholder="Elegir plantilla..." /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Ninguna (configuracion manual)</SelectItem>
                                    {Object.entries(seatTemplatesData).map(([k, t]) => (
                                        <SelectItem key={k} value={k}>{t.name} ({t.total_rows} filas)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Columnas izquierda</Label>
                                <Input placeholder="A,C" value={seatConfig.columns_left} onChange={e => setSeatConfig({...seatConfig, columns_left: e.target.value})} data-testid="seat-cols-left" />
                                <p className="text-[10px] text-muted-foreground">Ej. {'"A,C"'} o {'"A,B,C"'}</p>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Columnas derecha</Label>
                                <Input placeholder="D,F" value={seatConfig.columns_right} onChange={e => setSeatConfig({...seatConfig, columns_right: e.target.value})} data-testid="seat-cols-right" />
                                <p className="text-[10px] text-muted-foreground">Ej. {'"D,F"'} o {'"D,E,F"'}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Total de filas *</Label>
                                <Input type="number" min="1" max="80" value={seatConfig.total_rows} onChange={e => setSeatConfig({...seatConfig, total_rows: e.target.value})} data-testid="seat-total-rows" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Filas de salida</Label>
                                <Input placeholder="1,12,13" value={seatConfig.exit_rows} onChange={e => setSeatConfig({...seatConfig, exit_rows: e.target.value})} data-testid="seat-exit-rows" />
                                <p className="text-[10px] text-muted-foreground">Separadas por coma</p>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Asientos bloqueados</Label>
                            <Input placeholder="1A,3F,15B" value={seatConfig.blocked_seats} onChange={e => setSeatConfig({...seatConfig, blocked_seats: e.target.value})} data-testid="seat-blocked" />
                            <p className="text-[10px] text-muted-foreground">Opcional. Asientos que no se pueden asignar (ej. tripulacion, roto, extra legroom no-vender).</p>
                        </div>
                        {/* Preview count */}
                        <div className="rounded-lg bg-muted/40 border border-border/50 px-3 py-2 text-xs">
                            <strong>Capacidad estimada:</strong>{' '}
                            <span data-testid="seat-capacity-preview">
                                {(() => {
                                    const cl = (seatConfig.columns_left || '').split(',').filter(Boolean).length;
                                    const cr = (seatConfig.columns_right || '').split(',').filter(Boolean).length;
                                    const rows = parseInt(seatConfig.total_rows, 10) || 0;
                                    const blocked = (seatConfig.blocked_seats || '').split(',').filter(Boolean).length;
                                    return Math.max(0, (cl + cr) * rows - blocked);
                                })()} asientos
                            </span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSeatConfigDialog(null)}>Cancelar</Button>
                        <Button onClick={saveSeatConfig} disabled={savingSeatConfig} className="btn-primary" data-testid="save-seat-config">
                            {savingSeatConfig ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <LayoutGrid className="w-4 h-4 mr-2" />}
                            Guardar Mapa
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
